// "Your meeting's ready" share card (design ref: Google Meet instant meeting).
// Shown to the host once, right after they create an instant meeting, so they
// can pull people in without leaving the call. It is purely a share surface —
// dismissing loses nothing, since "Copy invite link" in the controls reopens
// the same link. Reuses Button/IconButton + the shared meetingShareLink helper.

import { memo, useCallback, useEffect, useState } from 'react';

import { api, meetingShareLink, readModelBSecret } from '../api';
import { playMeetingStart } from '../sounds';
import { pushToast } from '../store/callStore';
import { Button } from '../ui/Button';
import { IconButton } from '../ui/IconButton';
import { IconCheck, IconClose, IconFiles, IconLock, IconPeople } from '../ui/icons';
import { EmailChips } from './EmailChips';

export const MeetingReadyCard = memo(function MeetingReadyCard({
  slug,
  joinedAs,
  onClose,
}: {
  slug: string;
  joinedAs: string;
  onClose: () => void;
}) {
  // The card lives on the meeting page, so the current URL *is* the invite link
  // (the #k= fragment, if any, is already here for Model B meetings).
  const link = meetingShareLink(window.location.pathname, readModelBSecret());
  const zeroKnowledge = readModelBSecret() !== null;
  const [copied, setCopied] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [sending, setSending] = useState(false);

  // A warm chime the moment your instant meeting opens (this card mounts once).
  useEffect(() => {
    playMeetingStart();
  }, []);

  const startInvite = useCallback(() => setInviting(true), []);
  const cancelInvite = useCallback(() => {
    setInviting(false);
    setEmails([]);
  }, []);
  const sendInvites = useCallback(() => {
    if (!emails.length) return;
    setSending(true);
    api
      .meetingInvite(slug, emails)
      .then((r) => {
        pushToast(`Invited ${r.sent} ${r.sent === 1 ? 'person' : 'people'} by email`);
        setInviting(false);
        setEmails([]);
      })
      .catch((e: unknown) => pushToast(e instanceof Error ? e.message : 'Could not send', 'error'))
      .finally(() => setSending(false));
  }, [slug, emails]);

  const copy = useCallback(() => {
    void navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      pushToast('Invite link copied');
      window.setTimeout(() => setCopied(false), 2000);
    });
  }, [link]);

  // "Add others" (v1): the native share sheet on mobile, clipboard elsewhere.
  const share = useCallback(() => {
    if (navigator.share) {
      void navigator.share({ title: 'Brizina meeting', url: link }).catch(() => undefined);
      return;
    }
    copy();
  }, [link, copy]);

  return (
    <div className="bz-ready-card" role="dialog" aria-label="Your meeting is ready">
      <div className="bz-ready-head">
        <h2 className="bz-ready-title">Your meeting&rsquo;s ready</h2>
        <IconButton shape="flat" label="Close" onClick={onClose}>
          <IconClose size={14} />
        </IconButton>
      </div>

      {zeroKnowledge ? (
        <Button variant="primary" className="bz-ready-add" onClick={share}>
          <IconPeople size={17} />
          Add others
        </Button>
      ) : inviting ? (
        <div className="bz-ready-invite">
          <EmailChips
            emails={emails}
            onChange={setEmails}
            placeholder="name@example.com — Enter to add"
          />
          <div className="bz-ready-invite-actions">
            <Button onClick={cancelInvite}>Cancel</Button>
            <Button variant="primary" onClick={sendInvites} disabled={sending || !emails.length}>
              {sending ? 'Sending…' : 'Send invites'}
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="primary" className="bz-ready-add" onClick={startInvite}>
          <IconPeople size={17} />
          Add others by email
        </Button>
      )}

      <p className="bz-ready-desc">
        Or share this meeting link with others you want in the meeting
      </p>

      <div className="bz-ready-linkrow">
        <span className="bz-ready-link">{link}</span>
        <IconButton shape="flat" label="Copy link" onClick={copy}>
          {copied ? <IconCheck size={16} /> : <IconFiles size={16} />}
        </IconButton>
      </div>

      <div className="bz-ready-note">
        <IconLock size={15} />
        <span>
          People who use this link must get your approval before they can join.
          {zeroKnowledge &&
            ' This link also carries the encryption key — share it only over a channel you trust.'}
        </span>
      </div>

      {joinedAs && <p className="bz-ready-joined">Joined as {joinedAs}</p>}
    </div>
  );
});
